import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ScienceIcon from "@mui/icons-material/Science";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

export default function SolutionsPage() {
  const navigate = useNavigate();

  const solutions = [
    {
      icon: <LocalHospitalIcon sx={{ fontSize: 60 }} />,
      title: "For Healthcare Providers",
      subtitle: "Physicians, Specialists & Clinics",
      color: "#1a237e",
      gradient: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
      description:
        "Empower your practice with tools that help you match patients to clinical trials, manage patient information, and stay current on treatment guidelines.",
      features: [
        "Patient-trial matching tools",
        "Clinical trial database access",
        "Patient education resources",
        "Analytics & reporting",
        "Secure patient communication",
        "Referral management",
      ],
    },
    {
      icon: <ScienceIcon sx={{ fontSize: 60 }} />,
      title: "For Researchers",
      subtitle: "Clinical Researchers & Pharma",
      color: "#00897b",
      gradient: "linear-gradient(135deg, #00897b 0%, #00695c 100%)",
      description:
        "Accelerate your research with access to engaged patient communities, trial management tools, and data insights to improve trial enrollment.",
      features: [
        "Trial listing & management",
        "Patient recruitment tools",
        "Eligibility screening",
        "Data analytics",
        "Patient community access",
        "API integrations",
      ],
    },
    {
      icon: <BusinessIcon sx={{ fontSize: 60 }} />,
      title: "For Enterprises",
      subtitle: "Hospitals & Health Systems",
      color: "#f9a825",
      gradient: "linear-gradient(135deg, #f9a825 0%, #f57f17 100%)",
      description:
        "Deploy enterprise-grade healthcare platforms across your organization with white-label options, EHR integration, and dedicated support.",
      features: [
        "White-label deployment",
        "EHR integration",
        "Custom branding",
        "Dedicated account manager",
        "SLA guarantees",
        "HIPAA BAA included",
      ],
    },
  ];

  return (
    <Box>
      {/* HERO */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a237e 0%, #00897b 100%)",
          color: "white",
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Chip
            label="Solutions"
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "white",
              fontWeight: 600,
              mb: 3,
            }}
          />
          <Typography variant="h2" fontWeight={800} gutterBottom>
            Built for Everyone
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9, fontWeight: 400 }}>
            Whether you're a patient, provider, researcher, or enterprise — we
            have the right solution for you.
          </Typography>
        </Container>
      </Box>

      {/* SOLUTIONS */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8fafc" }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {solutions.map((solution, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-8px)", boxShadow: 8 },
                  }}
                >
                  <Box
                    sx={{
                      background: solution.gradient,
                      p: 4,
                      color: "white",
                      textAlign: "center",
                    }}
                  >
                    <Box sx={{ color: "rgba(255,255,255,0.9)", mb: 2 }}>
                      {solution.icon}
                    </Box>
                    <Typography variant="h5" fontWeight={700}>
                      {solution.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                      {solution.subtitle}
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                      sx={{ lineHeight: 1.7 }}
                    >
                      {solution.description}
                    </Typography>
                    {solution.features.map((feature, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: solution.color,
                          }}
                        />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate("/contact")}
                      sx={{
                        mt: 3,
                        background: solution.gradient,
                        fontWeight: 700,
                        "&:hover": { opacity: 0.9 },
                      }}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
